/**
 * Statistics — what the agents of this organization's installs spent, what came
 * of it, and what that says. The same reading as the app's page — four key
 * figures with their trend, To act on sorted by what is at stake, Worth knowing,
 * THE FIGURES — computed by the same functions over every machine of a member.
 *
 * A member reads their own days; an owner or an administrator reads the whole
 * organization first, then how members stand against everyone's median — no
 * rank — and any one member by picking them. The Worker decides which
 * (`readOrgUsage`), never this page. A verb that changes an organization's
 * setting is the administrators' alone: a member keeps the suggestion and is
 * told who sets it.
 *
 * A personal account has no row for this page; reached by its address, it says
 * where the statistics are read instead.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import type { Finding } from '@airlock/contracts/usage/findings';
import { USAGE_RANGES, type OrgUsageAnswer } from '@airlock/contracts/usage/report';
import { color, font } from '../../theme/tokens';
import type { PageId } from '../../components/nav';
import { api, apiPath } from '../../lib/api';
import type { Organization } from '../../lib/types';
import { failureText } from './shared';
import { FigureTabs, FindingGroup, GroupHead, KEYFRAMES, KeyFigures, MemberTable, enter, type FigureTab } from './usage-parts';
import { Segmented, filterSelect } from '../../components/FilterBar';
import { screenOf, windowOf } from './usage-view';

const STYLES = `${KEYFRAMES}
.usage-row:hover { background: #FAFAFC; }
.usage-row:focus-visible { box-shadow: inset 0 0 0 2px rgba(31,182,166,.35) !important; }
.usage-tab:hover, .usage-sort:hover, .usage-period:hover { color: #12161C !important; }
.usage-verb:hover { background: #FAFAFC !important; }
`;

export function UsagePage({
  organization,
  admin,
  email,
  onGo
}: {
  organization: Organization | null;
  admin: boolean;
  /** The reader's own account e-mail — a member's scope line. */
  email: string;
  onGo: (page: PageId, section?: string | null) => void;
}): React.JSX.Element {
  const [days, setDays] = useState<number>(30);
  const [member, setMember] = useState<string>('');
  const [answer, setAnswer] = useState<OrgUsageAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<FigureTab>('pair');
  const [lit, setLit] = useState<{ key: string; n: number } | null>(null);
  const [roll, setRoll] = useState(0);
  const figures = useRef<HTMLDivElement>(null);
  const span = useMemo(() => windowOf(days), [days]);

  useEffect(() => {
    if (!organization) return;
    setError(null);
    api
      .get<OrgUsageAnswer>(apiPath`/orgs/${organization.id}/usage?from=${span.from}&to=${span.to}`)
      .then(setAnswer)
      .catch((failure: unknown) => setError(failureText(failure, 'The account area did not answer. Nothing is lost — reload the page to try again.')));
  }, [organization?.id, span.from, span.to]);

  const only = answer?.everyone ? member || null : null;
  const screen = useMemo(() => (answer ? screenOf(answer, days, span.today, only) : null), [answer, days, span.today, only]);
  const replay = (): void => setRoll((n) => n + 1);

  if (!organization) {
    return (
      <Shell>
        <Blank
          title="Statistics stay on your computer"
          text="A personal account sends nothing here: what the agents spent and what came of it is kept by the app, on each machine. Open Statistics in Airlock — menu Airlock › Statistics."
        />
      </Shell>
    );
  }
  const org = organization.name;
  const everyone = !!answer?.everyone;
  const own = answer && !everyone ? answer.members[0] : undefined;
  const machines = own?.machines ?? 0;

  const lede = <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: color.inkSubtle }}>What the agents spent, what came of it, and what that says.</p>;
  const filters = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {everyone && answer.members.length ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: font.mono, fontSize: 10.5, fontWeight: 500, letterSpacing: '.12em', color: color.inkSubtle }}>MEMBER</span>
          <select
            aria-label="Member"
            value={member}
            onChange={(event) => {
              setMember(event.target.value);
              replay();
            }}
            style={{ ...filterSelect(Boolean(member)), width: 300, maxWidth: '100%', cursor: 'pointer' }}
          >
            <option value="">
              Everyone · {answer.members.length} member{answer.members.length === 1 ? '' : 's'} sending
            </option>
            {answer.members.map((one) => (
              <option key={one.email} value={one.email}>
                {one.email}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {answer && !everyone ? (
        <span style={{ fontSize: 13, color: color.inkMuted }}>
          Yours — {email}
          {machines ? `, from ${machines} machine${machines === 1 ? '' : 's'}` : ''}
        </span>
      ) : null}
      <span style={{ flex: 1 }} />
      <Segmented
        label="Period"
        value={String(days)}
        options={USAGE_RANGES.map((n) => ({ id: String(n), label: `${n} days` }))}
        onChange={(id) => {
          setDays(Number(id) as typeof days);
          setLit(null);
          replay();
        }}
      />
    </div>
  );

  // The states the page can be in before there is a report to read.
  let blank: { title: string; text: string; verb?: React.ReactNode } | null = null;
  if (error) blank = { title: 'The statistics could not be read', text: error, verb: <BlankVerb onClick={() => window.location.reload()}>Reload</BlankVerb> };
  else if (answer && !answer.collects && !answer.members.length) {
    blank = everyone
      ? {
          title: `${org} does not collect statistics`,
          text: 'Members’ apps keep their own, on their machines, and nothing arrives here. Turn collection on in Agents › All agents, then publish.',
          verb: <BlankVerb onClick={() => onGo('orgAgents', 'all')}>Open All agents</BlankVerb>
        }
      : { title: `${org} does not collect statistics`, text: `Your app keeps its own, on your computer — open Statistics in Airlock. Nothing arrives here until ${org} collects them.` };
  } else if (answer && !answer.members.length) {
    blank = everyone
      ? {
          title: 'No member has sent statistics yet',
          text: `${org} collects them since v${answer.version}${answer.publishedAt ? `, published ${ago(answer.publishedAt)}` : ''}. A member’s app sends once a day after its next check — the first days arrive tomorrow at the latest.`
        }
      : {
          title: 'Nothing from you yet',
          text: `${org} collects statistics, and none of your machines has sent a day yet. Your app sends once a day, after its first day of work — or its statistics are off on that machine.`
        };
  }

  const onVerb = (f: Finding): void => {
    const verb = f.verb;
    if (!verb) return;
    if (verb.go === 'workflows') return onGo('orgWorkflows');
    if (verb.go === 'agent') return onGo('orgAgents', verb.agent ?? 'claude-code');
    setTab(verb.go === 'day' ? 'day' : 'pair');
    if (f.target) setLit({ key: f.target, n: (lit?.n ?? 0) + 1 });
    requestAnimationFrame(() => figures.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
  };

  const scope = only ? 'this member’s' : everyone ? 'the organization’s' : 'your';
  const foot = !everyone
    ? `Sent by your ${machines > 1 ? `${machines} machines` : 'machine'} once a day; your app keeps the same days.`
    : only
      ? 'Sent by this member’s machines once a day.'
      : 'Sent by members’ machines once a day — a member whose app has statistics off sends nothing.';

  return (
    <Shell>
      {lede}
      {blank && !error ? null : filters}
      {blank ? <Blank {...blank} /> : null}
      {!blank && !screen ? <Skeleton /> : null}
      {!blank && screen ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {only ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '10px 14px', border: `1px solid ${color.accentRule}`, borderRadius: 8, background: color.noteTealBg, fontSize: 13, color: color.inkBody }}>
              <span>Showing one member:</span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5, fontWeight: 500, color: color.ink, overflowWrap: 'anywhere' }}>{only}</span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="usage-verb"
                onClick={() => {
                  setMember('');
                  replay();
                }}
                style={{ height: 24, padding: '0 9px', border: `1px solid ${color.line}`, borderRadius: 6, background: color.surface, fontFamily: 'inherit', fontSize: 12, color: color.inkBody, cursor: 'pointer' }}
              >
                Everyone
              </button>
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: font.mono, fontSize: 12.5, fontWeight: 500, color: color.ink }}>{screen.words.range}</span>
            <span style={{ fontSize: 12.5, color: color.inkSubtle }}>{screen.words.against} · at API list prices</span>
          </div>
          <FindingGroup
            title="To act on"
            sub={`What costs more than it needs to, or fails, in ${scope} work — sorted by what is at stake.`}
            items={screen.act}
            empty="Nothing to act on in this period."
            act
            roll={roll}
            readOnly={!admin}
            org={org}
            onVerb={onVerb}
          />
          {/* A page of figures opens on what to act on, sorted by what is at
              stake; the strip comes after it (redesign §42.3). */}
          <KeyFigures figures={screen.figures} roll={roll} fresh={screen.fresh} />
          <FindingGroup
            title="Worth knowing"
            sub="How the work went, against the period before."
            items={screen.know}
            empty="Nothing yet."
            act={false}
            roll={roll}
            readOnly={!admin}
            org={org}
            onVerb={onVerb}
          />
          {screen.members ? (
            <section aria-label="Across members" style={{ display: 'flex', flexDirection: 'column', animation: enter(roll) }}>
              <GroupHead title="Across members" sub="Each person beside the median of everyone — no ranking. Open a row to read that member’s findings." />
              {screen.members.rows.length < 2 ? (
                <div style={{ padding: '14px 0', fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>
                  A comparison needs two members sending — only {screen.members.rows[0]?.member ?? 'one'} does so far.
                </div>
              ) : null}
              {screen.memberFindings.map((m) => (
                <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '104px minmax(0, 1fr)', gap: '0 20px', padding: '12px 0', boxShadow: `inset 0 -1px 0 ${color.hairSoft}` }}>
                  <span style={{ fontFamily: font.mono, fontSize: 10.5, fontWeight: 500, letterSpacing: '.1em', color: color.inkSubtle, paddingTop: 3 }}>{m.subject}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 14.5, lineHeight: 1.5, color: color.ink }}>{m.text}</span>
                    <span style={{ fontSize: 12.5, color: color.inkSubtle }}>{m.evidence}</span>
                  </span>
                </div>
              ))}
              <MemberTable
                spread={screen.members}
                onOpen={(who) => {
                  setMember(who);
                  replay();
                }}
              />
              {answer?.silent.length ? (
                <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>
                  {answer.silent.length} member{answer.silent.length === 1 ? ' sends' : 's send'} nothing: {answer.silent.join(', ')}.{' '}
                  {answer.held
                    ? 'Collection is held on: their apps send from their next check.'
                    : 'Each member chooses, and their app has statistics off.'}
                </div>
              ) : null}
            </section>
          ) : null}
          <div ref={figures} style={{ scrollMarginTop: 12 }}>
            <FigureTabs
              data={screen.tables}
              tab={tab}
              onTab={(next) => {
                setTab(next);
                setLit(null);
              }}
              lit={lit?.key ?? null}
              litKey={lit?.n ?? 0}
            />
          </div>
          <p style={{ margin: 0, maxWidth: 760, fontSize: 12, lineHeight: 1.5, color: color.inkSubtle }}>
            Cost is worked out at each model’s public API prices, per token — Codex approximately, at GPT-5 rates. It is what the same tokens would cost on the API, not what a
            subscription bills. {foot}
          </p>
        </div>
      ) : null}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1044, fontVariantNumeric: 'tabular-nums' }}>
      <style>{STYLES}</style>
      {children}
    </div>
  );
}

function Blank({ title, text, verb }: { title: string; text: string; verb?: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ padding: '56px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center', border: `1px solid ${color.line}`, borderRadius: 9, background: color.surface, animation: 'usageFade .14s ease' }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: color.ink }}>{title}</span>
      <span style={{ maxWidth: 500, fontSize: 13, lineHeight: 1.5, color: color.inkMuted, textWrap: 'pretty' }}>{text}</span>
      {verb ?? null}
    </div>
  );
}

function BlankVerb({ onClick, children }: { onClick: () => void; children: React.ReactNode }): React.JSX.Element {
  return (
    <button
      type="button"
      className="usage-verb"
      onClick={onClick}
      style={{ marginTop: 8, height: 32, padding: '0 12px', border: `1px solid ${color.line}`, borderRadius: 8, background: 'transparent', fontFamily: 'inherit', fontSize: 13, color: color.inkBody, cursor: 'pointer' }}
    >
      {children}
    </button>
  );
}

function Skeleton(): React.JSX.Element {
  const pulse = 'usageSkel 1.6s ease-in-out infinite';
  return (
    <div aria-busy="true" aria-label="Reading the statistics" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', border: `1px solid ${color.line}`, borderRadius: 9, background: color.surface }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10, boxShadow: `inset -1px 0 0 ${color.hairSoft}` }}>
            <span style={{ width: 80, height: 10, borderRadius: 4, background: color.hairSoft, animation: pulse }} />
            <span style={{ width: 120, height: 24, borderRadius: 4, background: color.hairSoft, animation: pulse }} />
            <span style={{ width: 160, height: 10, borderRadius: 4, background: color.hairSoft, animation: pulse }} />
          </div>
        ))}
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{ height: 64, borderRadius: 6, background: color.hairSoft, animation: pulse }} />
      ))}
    </div>
  );
}

/** `3 days ago`, `today` — when a version was published. */
function ago(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
}
